import { IImportAgencyDataApiDuplicateReason, IImportAgencyDataApiResponse } from "models/axiosModel"
import { buildCsv } from "util/export/csvExport"

/** Required by `POST /agency/import-agency-data`. Kept separate from the export column config on purpose. */
export const AGENCY_IMPORT_HEADERS: string[] = ["Agency", "Office", "Agent", "Phone", "Email"]

export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024

/** Rows shown inside an expanded summary section before "…and N more". */
export const IMPORT_SECTION_PREVIEW_LIMIT = 3

const ACCESS_DENIED_CODE = 9020
const INVALID_AUTH_CODE = -999

export interface IAgencyImportFileCheck {
    errors: string[]
    dataRowCount: number
}

/** Skipped row as shown in a summary section / side panel. `detail` = duplicate reason or issue text. */
export interface IAgencyImportIssueRow {
    row: number
    agency_name: string
    office_name: string
    agent_name: string
    detail: string
}

export interface IAgencyImportResult {
    totalRecords: number
    duplicateCount: number
    /** missing_field_count + invalid_format_count, as the API reports them. */
    missingOrInvalidCount: number
    importedCount: number
    duplicateRows: IAgencyImportIssueRow[]
    missingOrInvalidRows: IAgencyImportIssueRow[]
}

const normalizeHeader = (value: string): string => value.trim().toLowerCase()

/** Minimal RFC 4180 parser: quoted cells, escaped quotes, CRLF/LF, leading BOM. */
export const parseCsvRows = (text: string): string[][] => {
    const source = text.replace(/^﻿/, "")
    const rows: string[][] = []
    let row: string[] = []
    let cell = ""
    let inQuotes = false

    for (let index = 0; index < source.length; index++) {
        const char = source[index]

        if (inQuotes) {
            if (char === '"' && source[index + 1] === '"') {
                cell += '"'
                index++
            } else if (char === '"') {
                inQuotes = false
            } else {
                cell += char
            }
            continue
        }

        if (char === '"') {
            inQuotes = true
        } else if (char === ",") {
            row.push(cell)
            cell = ""
        } else if (char === "\n" || char === "\r") {
            if (char === "\r" && source[index + 1] === "\n") index++
            row.push(cell)
            rows.push(row)
            row = []
            cell = ""
        } else {
            cell += char
        }
    }

    if (cell !== "" || row.length > 0) {
        row.push(cell)
        rows.push(row)
    }

    return rows
}

/** Header-only CSV the user fills in. */
export const buildAgencyImportTemplateCsv = (): string =>
    buildCsv<Record<string, string>>(
        [],
        AGENCY_IMPORT_HEADERS.map((header) => ({ key: header.toLowerCase(), header, getValue: () => "" })),
    )

const isBlankRow =(row: string[]): boolean => row.every((cell) => cell.trim() === "")

export const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** File-level checks before upload. Row-level rules stay with the backend. */
export const checkAgencyImportFile = (fileName: string, fileSize: number, text: string): IAgencyImportFileCheck => {
    if (!fileName.toLowerCase().endsWith(".csv")) {
        return { errors: ["Only .csv files are supported."], dataRowCount: 0 }
    }
    if (fileSize > MAX_IMPORT_FILE_BYTES) {
        return { errors: [`File is too large. Maximum size is ${formatFileSize(MAX_IMPORT_FILE_BYTES)}.`], dataRowCount: 0 }
    }

    const rows = parseCsvRows(text).filter((row) => !isBlankRow(row))
    if (rows.length === 0) {
        return { errors: ["The file is empty."], dataRowCount: 0 }
    }

    const headers = new Set(rows[0].map(normalizeHeader))
    const missingHeaders = AGENCY_IMPORT_HEADERS.filter((header) => !headers.has(normalizeHeader(header)))
    const dataRowCount = rows.length - 1
    const errors: string[] = []

    if (missingHeaders.length > 0) {
        errors.push(`Missing required column${missingHeaders.length > 1 ? "s" : ""}: ${missingHeaders.join(", ")}.`)
    }
    if (dataRowCount === 0) {
        errors.push("The file has no data rows below the header.")
    }

    return { errors, dataRowCount }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value)

const toArray = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value) ? value.filter(isRecord) : []

const toCount = (value: unknown, fallback: number): number => {
    const count = Number(value)
    return Number.isFinite(count) && count >= 0 ? count : fallback
}

const DUPLICATE_REASON_LABELS: Record<IImportAgencyDataApiDuplicateReason, string> = {
    already_exists: "Already exists",
    duplicate_in_file: "Repeated in file",
}

const duplicateReasonLabel = (reason: unknown): string =>
    DUPLICATE_REASON_LABELS[reason as IImportAgencyDataApiDuplicateReason] ?? "Duplicate"

/** "Agent is required, Email format is invalid". Falls back to the field name when the message is blank. */
const formatRowIssues = (issues: unknown): string =>
    toArray(issues)
        .map((issue) => String(issue.message ?? "").trim() || `${String(issue.field ?? "Field")} is invalid`)
        .join(", ")

const toIssueRow = (item: Record<string, unknown>, detail: string): IAgencyImportIssueRow => ({
    row: Number(item.row) || 0,
    agency_name: String(item.agency_name ?? ""),
    office_name: String(item.office_name ?? ""),
    agent_name: String(item.agent_name ?? ""),
    detail,
})

const byRow = (left: IAgencyImportIssueRow, right: IAgencyImportIssueRow): number => left.row - right.row

/** Success `data` → summary counts + rows for the Duplicate and Missing/Invalid sections. */
export const parseAgencyImportResult = (data: IImportAgencyDataApiResponse["data"]): IAgencyImportResult => {
    const source: Record<string, unknown> = isRecord(data) ? data : {}
    const summary: Record<string, unknown> = isRecord(source.summary) ? source.summary : {}

    const duplicateRows = toArray(source.duplicate)
        .map((item) => toIssueRow(item, duplicateReasonLabel(item.reason)))
        .sort(byRow)
    const missingRows = toArray(source.missing_field).map((item) => toIssueRow(item, formatRowIssues(item.issues)))
    const invalidRows = toArray(source.invalid_format).map((item) => toIssueRow(item, formatRowIssues(item.issues)))
    const missingOrInvalidRows = [...missingRows, ...invalidRows].sort(byRow)

    const duplicateCount = toCount(summary.duplicate_count, duplicateRows.length)
    const missingOrInvalidCount =
        toCount(summary.missing_field_count, missingRows.length) + toCount(summary.invalid_format_count, invalidRows.length)
    const importedCount = toCount(summary.ready_to_import, 0)
    const totalRecords = toCount(summary.total_records, duplicateCount + missingOrInvalidCount + importedCount)

    return { totalRecords, duplicateCount, missingOrInvalidCount, importedCount, duplicateRows, missingOrInvalidRows }
}

export interface IAgencyImportRowPreview {
    rows: IAgencyImportIssueRow[]
    hiddenCount: number
}

/** First rows shown inside an expanded section; the rest open in the side panel. */
export const getImportRowPreview = (
    rows: IAgencyImportIssueRow[],
    limit: number = IMPORT_SECTION_PREVIEW_LIMIT,
): IAgencyImportRowPreview => ({
    rows: rows.slice(0, limit),
    hiddenCount: Math.max(0, rows.length - limit),
})

/** Side-panel search over agency, office and agent names. */
export const filterImportIssueRows = (rows: IAgencyImportIssueRow[], query: string): IAgencyImportIssueRow[] => {
    const needle = query.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((row) =>
        [row.agency_name, row.office_name, row.agent_name].some((value) => value.toLowerCase().includes(needle)),
    )
}

const getMissingColumns = (data: IImportAgencyDataApiResponse["data"]): string[] =>
    isRecord(data) && Array.isArray(data.missing_columns)
        ? data.missing_columns.filter((column): column is string => typeof column === "string")
        : []

export const getImportErrorMessage = (response: IImportAgencyDataApiResponse | undefined): string => {
    const error = response?.error
    if (typeof error === "string" && /"status":\s*401/.test(error)) {
        return "Your session has expired. Please log in again."
    }
    if (!error || typeof error === "string") {
        return "Import failed. Please check your connection and try again."
    }
    if (error.code === ACCESS_DENIED_CODE) return "Only admins can import agency data."
    const missingColumns = getMissingColumns(response?.data)
    if (missingColumns.length > 0) {
        return `Missing required column${missingColumns.length > 1 ? "s" : ""}: ${missingColumns.join(", ")}.`
    }
    if (error.code === INVALID_AUTH_CODE || error.statusCode === INVALID_AUTH_CODE) {
        return "Your session has expired. Please log in again."
    }
    return error.message || "Import failed. Please try again."
}
